import { useCallback, useState } from 'react';
import isEqual from 'react-fast-compare';
import { useIntl } from 'react-intl';

import { errorHandler } from './utilities';

export const ERROR = 'error';

const initialErrors = {};
const initialTouched = {};

const defaultYupOptions = {
  abortEarly: false,
};

// NB: It's _really important_ that these inputs be memoized to prevent infinite re-validation
export const useYup = ({ options = defaultYupOptions }) => {
  const intl = useIntl();
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState(initialTouched);

  const validate = useCallback(
    (form, formSchema) => {
      try {
        formSchema.validateSync(form, options);
        setErrors(initialErrors);
        return true;
      } catch (e) {
        setErrors((previousErrors) => {
          const updatedErrors = errorHandler(intl, e);
          return isEqual(previousErrors, updatedErrors) ? previousErrors : updatedErrors;
        });
        return false;
      }
    },
    [options, intl],
  );

  const setFieldTouched = useCallback((field, isTouched = true) => {
    setTouched((prev) => ({
      ...prev,
      [field]: isTouched,
    }));
  }, []);

  const setFieldsTouched = useCallback((fields, isTouched = true) => {
    setTouched((prev) => {
      const updates = fields.reduce((acc, field) => {
        return { ...acc, [field]: isTouched };
      }, {});
      return { ...prev, ...updates };
    });
  }, []);

  const getFieldError = useCallback(
    (field) => {
      return touched[field] ? errors[field] : undefined;
    },
    [errors, touched],
  );

  return {
    validate,
    errors,
    touched,
    setFieldTouched,
    setFieldsTouched,
    getFieldError,
  };
};
